/*
 * Copyright © 2020-2025 Ocean Galaxy Inc. All Rights Reserved.
 * @Description: 精准搜索照片列表状态管理
 */
import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit'
import { RootState } from '../app/store'
import { Modal } from 'antd'
import { capturePromiseException } from '../utils/sentryUtils'
import { buildParamsWithAuthPolicy } from '../actions/photo'
import { Photo } from '../global'
import fetchHelper from '../utils/fetchHelper'
import { enableMapSet } from 'immer'

enableMapSet()

// 声明 Vite define 定义的全局变量
declare const __API_SERVER_HOST__: string

// 定义初始状态类型
interface PhotosByPreciseState {
  preciseSearch: {
    canPreciseSearch: boolean
    notWords: string[]
  }
  showRedDot: boolean
  photos: Photo[]
  checkedPhotos: Set<number>
  unCheckedPhotos: Set<number>
  checkAll: boolean
  loading: boolean
  lastPageCond: string
  photosNum: number
  isAscSort: boolean
}

// 初始状态
const initialState: PhotosByPreciseState = {
  preciseSearch: {
    canPreciseSearch: false,
    notWords: []
  },
  showRedDot: false,
  photos: [],
  checkedPhotos: new Set<number>(),
  unCheckedPhotos: new Set<number>(),
  checkAll: false,
  loading: false,
  lastPageCond: '',
  photosNum: 0,
  isAscSort: false
}

// 创建异步 thunk
export const fetchPhotosByPrecise = createAsyncThunk(
  'photosByPrecise/fetchPhotos',
  async (data: any, { dispatch, getState, rejectWithValue }) => {
    try {
      const state = getState() as RootState
      const config = {
        url: __API_SERVER_HOST__ + '/v5/workgroup/photos',
        arguments: {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(buildParamsWithAuthPolicy(state, data))
        }
      }
      const result = await fetchHelper(dispatch, getState, config)
      const { payload, status } = result
      if (status === 0) {
        return {
          photos: payload.photos || [],
          expandInfo: payload.expandInfo || {},
          lastPageCond: payload.lastPageCond || '',
          photosNum: payload.total || 0
        }
      } else if (status === -3) {
        Modal.warning({
          title: '提示',
          content: '您已退出或被移出当前群',
          onOk: () => {
            window.location.href = '/'
          }
        })
        return rejectWithValue('您已退出或被移出当前群')
      } else if (status === -991) {
        Modal.warning({
          title: '提示',
          content: '暂无照片查看权限'
        })
        return {
          photos: [],
          expandInfo: {},
          lastPageCond: '',
          photosNum: 0
        }
      }

      return rejectWithValue('请求失败')
    } catch (error) {
      capturePromiseException(error)
      return rejectWithValue('网络错误')
    }
  }
)

// 创建加载更多照片的异步 thunk
export const fetchMorePhotosByPrecise = createAsyncThunk(
  'photosByPrecise/fetchMorePhotos',
  async (data: any, { dispatch, getState, rejectWithValue }) => {
    try {
      const state = getState() as RootState
      const config = {
        url: __API_SERVER_HOST__ + '/v5/workgroup/photos',
        arguments: {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(buildParamsWithAuthPolicy(state, data))
        }
      }
      const result = await fetchHelper(dispatch, getState, config)
      const { payload, status } = result

      if (status === 0) {
        return {
          photos: payload.photos || [],
          lastPageCond: payload.lastPageCond || ''
        }
      } else if (status === -3) {
        Modal.warning({
          title: '提示',
          content: '您已退出或被移出当前群',
          onOk: () => {
            window.location.href = '/'
          }
        })
        return rejectWithValue('您已退出或被移出当前群')
      } else if (status === -991) {
        Modal.warning({
          title: '提示',
          content: '暂无照片查看权限'
        })
        return rejectWithValue('暂无照片查看权限')
      }

      return rejectWithValue('请求失败')
    } catch (error) {
      capturePromiseException(error)
      return rejectWithValue('网络错误')
    }
  }
)

// 创建 slice
const photosByPreciseSlice = createSlice({
  name: 'photosByPrecise',
  initialState,
  reducers: {
    setPreciseSearch: (
      state,
      action: PayloadAction<{ canPreciseSearch: boolean; notWords: string[] }>
    ) => {
      state.preciseSearch = action.payload
    },
    setShowRedDot: (state, action: PayloadAction<boolean>) => {
      state.showRedDot = action.payload
    },
    setIsAscSort: (state, action: PayloadAction<boolean>) => {
      state.isAscSort = action.payload
    },
    pushCheckedPhoto: (state, action: PayloadAction<number>) => {
      state.checkedPhotos.add(action.payload)
    },
    removeCheckedPhoto: (state, action: PayloadAction<number>) => {
      state.checkedPhotos.delete(action.payload)
    },
    pushUncheckedPhoto: (state, action: PayloadAction<number>) => {
      state.unCheckedPhotos.add(action.payload)
    },
    removeUncheckedPhoto: (state, action: PayloadAction<number>) => {
      state.unCheckedPhotos.delete(action.payload)
    },
    setCheckAll: (state) => {
      state.checkAll = true
      state.checkedPhotos.clear()
      state.unCheckedPhotos.clear()
    },
    clearCheck: (state) => {
      state.unCheckedPhotos.clear()
      state.checkedPhotos.clear()
      state.checkAll = false
    },
    setPhotos: (state, action: PayloadAction<Photo[]>) => {
      state.photos = action.payload
    },
    reset: (state) => {
      state.photos = []
      state.checkedPhotos.clear()
      state.unCheckedPhotos.clear()
      state.checkAll = false
      state.loading = false
      state.lastPageCond = ''
      state.photosNum = 0
    }
  },
  extraReducers: (builder) => {
    builder
      // 处理 fetchPhotosByPrecise
      .addCase(fetchPhotosByPrecise.pending, (state) => {
        state.loading = true
      })
      .addCase(fetchPhotosByPrecise.fulfilled, (state, action) => {
        if (action.payload) {
          state.photos = action.payload.photos || []
          state.lastPageCond = action.payload.lastPageCond || ''
          state.photosNum = action.payload.photosNum || 0
          state.loading = false
        }
      })
      .addCase(fetchPhotosByPrecise.rejected, (state) => {
        state.loading = false
      })
      // 处理 fetchMorePhotosByPrecise
      .addCase(fetchMorePhotosByPrecise.fulfilled, (state, action) => {
        state.photos.push(...action.payload.photos)
        state.lastPageCond = action.payload.lastPageCond
      })
      .addCase(fetchMorePhotosByPrecise.rejected, (state) => {
        // 处理错误状态
      })
  }
})

// 导出 actions
export const {
  setPreciseSearch,
  setShowRedDot,
  setIsAscSort,
  pushCheckedPhoto,
  removeCheckedPhoto,
  pushUncheckedPhoto,
  removeUncheckedPhoto,
  setCheckAll,
  clearCheck,
  setPhotos,
  reset
} = photosByPreciseSlice.actions

// 导出 selectors
export const selectPreciseSearch = (state: RootState) =>
  state.photosByPrecise.preciseSearch

export const selectShowRedDot = (state: RootState) =>
  state.photosByPrecise.showRedDot

export const selectCheckedPhotos = (state: RootState) =>
  Array.from(state.photosByPrecise.checkedPhotos)

export const selectUncheckedPhotos = (state: RootState) =>
  Array.from(state.photosByPrecise.unCheckedPhotos)

export const selectCheckAll = (state: RootState) =>
  state.photosByPrecise.checkAll

export const selectPhotos = (state: RootState) => state.photosByPrecise.photos

export const selectLoading = (state: RootState) => state.photosByPrecise.loading

export const selectLastPageCond = (state: RootState) =>
  state.photosByPrecise.lastPageCond

export const selectPhotosNum = (state: RootState) =>
  state.photosByPrecise.photosNum

export const selectIsAscSort = (state: RootState) =>
  state.photosByPrecise.isAscSort

// 导出 reducer
export default photosByPreciseSlice.reducer
